import { useEffect, useState } from 'react';
import { Beaker, ShieldCheck, Lock, ChevronDown, Check } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/lib/auth';
import {
  PRODUCTION, SANDBOX, getWorkspace, setWorkspace, isSandboxLocked,
} from '@/lib/workspace';

/**
 * Workspace switcher dropdown.
 *
 * - Non-demo admins (e.g. `neerajsk.6@gmail.com`): can toggle freely between
 *   Production and Sandbox. The selection persists in localStorage.
 * - Demo admin (`aarav.mehta@example.com`): force-locked to Sandbox; the
 *   dropdown is replaced with a non-interactive "Sandbox · locked" pill.
 *
 * When Sandbox is active, an amber pill clearly indicates the elevated
 * state next to the user. The trigger button itself also gets an amber
 * outline so it's obvious from the topbar at a glance.
 */
export const WorkspaceSwitcher = () => {
  const { user } = useAuth();
  const locked = isSandboxLocked(user?.email);
  const [ws, setWs] = useState(() => (locked ? SANDBOX : getWorkspace()));

  // Keep state in sync with cross-tab / programmatic workspace updates.
  useEffect(() => {
    const onChange = () => setWs(locked ? SANDBOX : getWorkspace());
    window.addEventListener('rb-workspace-changed', onChange);
    window.addEventListener('storage', onChange);
    return () => {
      window.removeEventListener('rb-workspace-changed', onChange);
      window.removeEventListener('storage', onChange);
    };
  }, [locked]);

  // If the user is demo-locked, ensure storage matches reality.
  useEffect(() => {
    if (locked && getWorkspace() !== SANDBOX) setWorkspace(SANDBOX);
  }, [locked]);

  const isSandbox = ws === SANDBOX;

  const handleSelect = (next) => {
    if (locked) return;
    if (next === ws) return;
    setWorkspace(next);
    setWs(next);
    // Soft refresh: reload the page so cached admin data is re-fetched
    // with the new workspace header. Operators expect the entire admin
    // surface to re-hydrate when they switch lanes.
    window.location.reload();
  };

  // Demo: locked badge, no dropdown
  if (locked) {
    return (
      <Badge
        variant="outline"
        className="rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-300 border-amber-500/40 gap-1.5 px-2.5 shrink-0"
        data-testid="admin-workspace-locked-badge"
      >
        <Lock className="h-3 w-3" />
        <span className="text-[11px] font-medium tracking-wide">Sandbox · locked</span>
      </Badge>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={
            'h-8 px-2.5 gap-1.5 shrink-0 ' +
            (isSandbox
              ? 'border-amber-500/50 text-amber-600 dark:text-amber-300 hover:bg-amber-500/10 hover:text-amber-700 dark:hover:text-amber-200 bg-amber-500/10'
              : 'border-border text-foreground/80 hover:bg-[hsl(var(--surface-2))]')
          }
          data-testid="admin-workspace-switcher-trigger"
        >
          {isSandbox ? <Beaker className="h-3.5 w-3.5" /> : <ShieldCheck className="h-3.5 w-3.5" />}
          <span className="text-[11px] font-semibold tracking-wide uppercase">
            {isSandbox ? 'Sandbox' : 'Production'}
          </span>
          <ChevronDown className="h-3 w-3 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[240px]" data-testid="admin-workspace-switcher-menu">
        <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-muted-foreground">
          Switch workspace
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={(e) => { e.preventDefault(); handleSelect(PRODUCTION); }}
          className="gap-2"
          data-testid="admin-workspace-option-production"
        >
          <ShieldCheck className="h-4 w-4 text-emerald-500" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium">Production</div>
            <div className="text-[11px] text-muted-foreground">Live canonical library</div>
          </div>
          {ws === PRODUCTION && <Check className="h-4 w-4" />}
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={(e) => { e.preventDefault(); handleSelect(SANDBOX); }}
          className="gap-2"
          data-testid="admin-workspace-option-sandbox"
        >
          <Beaker className="h-4 w-4 text-amber-500" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium">Sandbox</div>
            <div className="text-[11px] text-muted-foreground">Isolated copy for experiments</div>
          </div>
          {ws === SANDBOX && <Check className="h-4 w-4" />}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/**
 * Standalone amber "SANDBOX" pill — rendered in the topbar (in addition to
 * the dropdown) so the elevated state is unmissable when active.
 */
export const SandboxBadge = () => {
  const { user } = useAuth();
  const locked = isSandboxLocked(user?.email);
  const [ws, setWs] = useState(() => (locked ? SANDBOX : getWorkspace()));

  useEffect(() => {
    const onChange = () => setWs(locked ? SANDBOX : getWorkspace());
    window.addEventListener('rb-workspace-changed', onChange);
    window.addEventListener('storage', onChange);
    return () => {
      window.removeEventListener('rb-workspace-changed', onChange);
      window.removeEventListener('storage', onChange);
    };
  }, [locked]);

  // The locked badge already shows "Sandbox · locked", so don't double-render.
  if (locked) return null;
  if (ws !== SANDBOX) return null;

  return (
    <Badge
      variant="outline"
      className="rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40 gap-1.5 px-2.5 shrink-0 animate-pulse"
      data-testid="admin-workspace-sandbox-pill"
    >
      <Beaker className="h-3 w-3" />
      <span className="text-[11px] font-semibold tracking-wide uppercase">Sandbox active</span>
    </Badge>
  );
};
